"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import LegalArticle from "@/components/sections/LegalArticle";
import LegalPageShell from "@/components/sections/LegalPageShell";
import { PUBLIC_CONTACT_EMAIL } from "@/content/company";
import { getLegalContent } from "@/content/legal";
import {
  applyGpcToConsentStore,
  isGpcEnabled,
  openCookiePreferences,
  readCookieConsent,
  writeCookieConsent,
} from "@/lib/cookie-consent";
import { useLocale } from "@/lib/i18n/provider";
import { localizePath } from "@/lib/i18n/routing";

/**
 * CCPA/CPRA “Do Not Sell or Share” page (DEF-US-02).
 * Opt-out writes the same cookie-consent store that gates analytics/marketing.
 */
export default function DoNotSellPage() {
  const { locale } = useLocale();
  const copy = getLegalContent(locale).doNotSell;
  const [gpc, setGpc] = useState(false);
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    applyGpcToConsentStore();
    const signal = isGpcEnabled();
    setGpc(signal);
    const stored = readCookieConsent();
    setOptedOut(
      signal ||
        (stored != null && stored.analytics === false && stored.marketing === false),
    );
  }, []);

  const optOut = () => {
    writeCookieConsent({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
    setOptedOut(true);
  };

  const emailHref = `mailto:${PUBLIC_CONTACT_EMAIL}?subject=${encodeURIComponent(copy.emailSubject)}`;

  return (
    <LegalPageShell>
      <LegalArticle
        eyebrow={copy.eyebrow}
        title={copy.title}
        lastUpdated={copy.lastUpdated}
      >
        <p>{copy.intro}</p>
        <h2>{copy.saleTitle}</h2>
        <p>{copy.saleBody}</p>
        <h2>{copy.gpcTitle}</h2>
        <p>{copy.gpcBody}</p>
        <p>
          <strong>{gpc ? copy.gpcDetected : copy.gpcNotDetected}</strong>
        </p>
        <p>
          {optedOut ? (
            <strong>{copy.optedOut}</strong>
          ) : (
            <button
              type="button"
              onClick={optOut}
              className="rounded-full bg-[var(--color-accent-base)] px-4 py-2 text-sm font-semibold text-[var(--color-accent-contrast)]"
            >
              {copy.optOutButton}
            </button>
          )}
        </p>
        <p>
          {copy.emailPrefix}{" "}
          <a href={emailHref}>{PUBLIC_CONTACT_EMAIL}</a> {copy.emailSuffix}
        </p>
        <p>
          {copy.prefsPrefix}{" "}
          <button
            type="button"
            onClick={() => openCookiePreferences()}
            className="font-medium text-[var(--color-text-primary)] underline underline-offset-2"
          >
            {copy.prefsLabel}
          </button>
          . {copy.privacyPrefix}{" "}
          <Link href={localizePath("/legal/privacy-policy", locale)}>
            {copy.privacyLabel}
          </Link>
          .
        </p>
      </LegalArticle>
    </LegalPageShell>
  );
}
